import {
  ENVIRONMENT_INITIALIZER,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  Provider,
} from '@angular/core';
import { Router, Routes } from '@angular/router';

import { Actions, ofType, provideEffects } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CandidatesDataSourceService } from 'apps/recruitee/src/app/features/candidate/views/candidates-data-source.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { JobActionsFacade } from 'apps/recruitee/src/app/features/offers/facades/job-actions.facade';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfferActionsFacade } from 'apps/recruitee/src/app/features/offers/facades/offer-actions-facade.types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ManageEeoBulkService } from 'apps/recruitee/src/app/features/offers/offers-page/manage-eeo-bulk/manage-eeo-bulk.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ManageReferralsBulkService } from 'apps/recruitee/src/app/features/offers/offers-page/manage-referrals-bulk/manage-referrals-bulk.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OffersBulkActionsExecutorService } from 'apps/recruitee/src/app/features/offers/offers-page/offers-bulk-actions-executor.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OffersBulkActionsValidatorService } from 'apps/recruitee/src/app/features/offers/offers-page/offers-bulk-actions-validator.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { JobsBoardCardComponent } from 'apps/recruitee/src/app/features/offers/views/board/card/jobs-board-card.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { JobsGroupHeaderComponent } from 'apps/recruitee/src/app/features/offers/views/board/group-header/group-header.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ViewsOffersBulkActionsController } from 'apps/recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions.controller';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfferListHeadingComponent } from 'apps/recruitee/src/app/features/offers/views/offer-list-item/offer-list-heading.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfferListWrapperComponent } from 'apps/recruitee/src/app/features/offers/views/offer-list-item/offer-list-wrapper.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { JobsDataSourceService } from 'apps/recruitee/src/app/features/offers/views/services/jobs-data-source.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ViewsJobsEffects } from 'apps/recruitee/src/app/features/offers/views/services/jobs.effects';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  offerColumnFieldMap,
  offerColumns,
  OfferTableFields,
} from 'apps/recruitee/src/app/features/offers/views/table-cells/offer-columns';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RtPricingFeatureConnectedModule } from 'apps/recruitee/src/app/features/pricing/connected/pricing-feature-connected.module';
import { merge, take, tap } from 'rxjs';

import { UserActions } from '@recruitee/account-data';
import { BulkActionsController, BulkActionsSelectionTracker } from '@recruitee/bulk-actions';
import { JobsAnalytics, OfferAnalytics } from '@recruitee/offers';
import type { OfferItem } from '@recruitee/offers-types';
import { RoleActions } from '@recruitee/roles-data';
import { DataSourceActions } from '@recruitee/views-data';
import {
  provideViewBaseRoute,
  provideListLayout,
  provideTableLayout,
  TableConfig,
  provideBoardLayout,
  dataSourceProviders,
} from '@recruitee/views-util';

const viewsEnvInitialize: Array<Provider | EnvironmentProviders> = [
  {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useFactory: () => {
      const store = inject(Store);
      const actions$ = inject(Actions);
      const router = inject(Router);

      merge(
        actions$.pipe(
          ofType(DataSourceActions.fetchOneFailed),
          tap(() => router.navigate(['/'])),
        ),
        actions$.pipe(ofType(DataSourceActions.fetchOneSuccess)),
      )
        .pipe(take(1))
        .subscribe();

      return () => {
        store.dispatch(UserActions.fetchCollection());
        store.dispatch(RoleActions.fetchCollection());
      };
    },
  },
];

const viewsProviders: Array<Provider | EnvironmentProviders> = [
  CandidatesDataSourceService,
  JobsDataSourceService,
  provideEffects(ViewsJobsEffects),
  dataSourceProviders('jobs', {
    jobs: JobsDataSourceService,
    candidates: CandidatesDataSourceService,
  }),
  provideViewBaseRoute('/views'),

  // To make offer card work
  { provide: OfferAnalytics, useClass: JobsAnalytics },
  JobActionsFacade,
  { provide: OfferActionsFacade, useExisting: JobActionsFacade },
  provideListLayout({
    jobs: { header: OfferListHeadingComponent, record: OfferListWrapperComponent },
  }),
  provideTableLayout({
    jobs: <TableConfig<OfferItem, OfferTableFields>>{
      tableGroupHeader: OfferListHeadingComponent,
      columns: offerColumns,
      columnFieldMap: offerColumnFieldMap,
    },
  }),
  provideBoardLayout({
    jobs: {
      item: JobsBoardCardComponent,
      groupHeader: JobsGroupHeaderComponent,
    },
  }),
  importProvidersFrom(RtPricingFeatureConnectedModule),
  // Offer bulk actions services
  ViewsOffersBulkActionsController,
  { provide: BulkActionsController, useExisting: ViewsOffersBulkActionsController },
  OffersBulkActionsExecutorService,
  OffersBulkActionsValidatorService,
  ManageEeoBulkService,
  ManageReferralsBulkService,
  BulkActionsSelectionTracker,
];

export const ROUTES: Routes = [
  {
    path: 'prosemirror',
    loadChildren: () => import('../prosemirror/prosemirror.module').then(m => m.ProsemirrorModule),
  },
  {
    path: 'views',
    loadChildren: () =>
      import('@recruitee/views').then(m =>
        m.provideDataSourceRouting([viewsEnvInitialize, viewsProviders], true),
      ),
  },
  {
    path: 'analytics',
    loadChildren: () => import('@recruitee/reports-feature').then(m => m.REPORTS_ROUTES),
  },
];
